import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Box, Card, CardContent, Typography, Grid, CircularProgress,
  Button, FormControl, InputLabel, Select, MenuItem, LinearProgress, Chip,
  Tooltip,
} from '@mui/material'
import { TrendingUp, Assessment, Star, Insights, AttachMoney } from '@mui/icons-material'
import { analysisApi, analyticsApi, tenantApi } from '../services/api'

interface ScannerVersionRow {
  scanner_version: string
  scan_count: number
  first_seen: string | null
  last_seen: string | null
  distinct_repos: number
}

interface IntentCategoryRow {
  category: string
  count: number
  agents: Array<{
    agent_id: string
    agent_name?: string | null
    framework?: string | null
    confidence?: number
  }>
}

interface IntentDistribution {
  total_agents_classified: number
  categories: IntentCategoryRow[]
  categories_available: string[]
  semantic_intent_active: boolean
}

// §22 — tenant-wide cost rollup (latest scan per repo)
interface CostRollup {
  cost_estimation_visible: boolean
  repos_total: number
  repos_priced: number
  repos_with_daily_estimate: number
  cost_per_request_usd: { low: number; high: number } | null
  cost_per_day_usd: { low: number; high: number } | null
}

const fmtCost = (v: number) => (v < 0.01 ? v.toFixed(4) : v.toFixed(2))

// 6 KB-aligned dimensions — must stay in sync with services/analysis DIMENSIONS
// and AnalysisDetailPage.DIMENSIONS so trend keys line up everywhere.
const DIMENSION_LABELS: Record<string, string> = {
  prompt_engineering:      'Prompt Engineering',
  agent_design:            'Agent Design',
  memory_architecture:     'Memory Architecture',
  orchestration_soundness: 'Orchestration',
  observability_tracing:   'Observability',
  governance_alignment:    'Governance',
}

const directionIcon = (d: string) =>
  d === 'improving' ? '↑' : d === 'declining' ? '↓' : '→'
const directionColor = (d: string) =>
  d === 'improving' ? '#4caf50' : d === 'declining' ? '#f44336' : '#9e9e9e'
const scoreColor = (s: number) => s >= 8 ? '#4caf50' : s >= 5 ? '#ff9800' : '#f44336'

const AnalyticsPage = () => {
  const [stats, setStats] = useState<any>(null)
  const [tenants, setTenants] = useState<any[]>([])
  const [tenantId, setTenantId] = useState('')
  const [trends, setTrends] = useState<any>(null)
  const [insights, setInsights] = useState<any[]>([])
  const [milestones, setMilestones] = useState<any[]>([])
  // RBAC-7 — per-tenant scanner-version distribution
  const [scannerVersions, setScannerVersions] = useState<ScannerVersionRow[]>([])
  // INT-10 — per-tenant intent classification distribution
  const [intentDist, setIntentDist] = useState<IntentDistribution | null>(null)
  // §22 — per-tenant cost rollup
  const [costRollup, setCostRollup] = useState<CostRollup | null>(null)
  const [loading, setLoading] = useState(true)
  const navigate = useNavigate()

  useEffect(() => {
    Promise.allSettled([
      analyticsApi.getStats(),
      tenantApi.getTenants(),
    ]).then(([statsRes, tenantsRes]) => {
      if (statsRes.status === 'fulfilled') setStats(statsRes.value.data)
      if (tenantsRes.status === 'fulfilled') setTenants(tenantsRes.value.data.tenants || tenantsRes.value.data || [])
    }).finally(() => setLoading(false))
  }, [])

  const fetchTenantAnalytics = async () => {
    if (!tenantId.trim()) return
    try {
      const [trendsRes, insightsRes, milestonesRes, scannerVersionsRes, intentDistRes, costRollupRes] = await Promise.allSettled([
        analyticsApi.getTenantTrends(tenantId),
        analyticsApi.getTenantInsights(tenantId),
        analyticsApi.getTenantMilestones(tenantId),
        analysisApi.getTenantScannerVersions(tenantId),
        analysisApi.getTenantIntentDistribution(tenantId),
        analysisApi.getTenantCostRollup(tenantId),
      ])
      if (trendsRes.status === 'fulfilled') setTrends(trendsRes.value.data)
      if (insightsRes.status === 'fulfilled') setInsights(insightsRes.value.data.insights || insightsRes.value.data || [])
      if (milestonesRes.status === 'fulfilled') setMilestones(milestonesRes.value.data.milestones || milestonesRes.value.data || [])
      if (scannerVersionsRes.status === 'fulfilled') {
        setScannerVersions(scannerVersionsRes.value.data.versions || [])
      } else {
        setScannerVersions([])
      }
      if (intentDistRes.status === 'fulfilled') {
        setIntentDist(intentDistRes.value.data as IntentDistribution)
      } else {
        setIntentDist(null)
      }
      if (costRollupRes.status === 'fulfilled') {
        setCostRollup(costRollupRes.value.data as CostRollup)
      } else {
        setCostRollup(null)
      }
    } catch (e) {
      console.error('Failed to fetch tenant analytics:', e)
    }
  }

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 3 }}>Analytics</Typography>

      {/* Global Stats */}
      {stats && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6} sm={3}>
            <Card><CardContent sx={{ textAlign: 'center' }}>
              <Assessment color="primary" sx={{ fontSize: 32, mb: 1 }} />
              <Typography variant="h4">{stats.total_assessments || 0}</Typography>
              <Typography variant="body2" color="text.secondary">Total Assessments</Typography>
            </CardContent></Card>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Card><CardContent sx={{ textAlign: 'center' }}>
              <TrendingUp color="success" sx={{ fontSize: 32, mb: 1 }} />
              <Typography variant="h4">{stats.total_tenants || 0}</Typography>
              <Typography variant="body2" color="text.secondary">Active Tenants</Typography>
            </CardContent></Card>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Card><CardContent sx={{ textAlign: 'center' }}>
              <Star color="warning" sx={{ fontSize: 32, mb: 1 }} />
              <Typography variant="h4">{stats.total_repositories || 0}</Typography>
              <Typography variant="body2" color="text.secondary">Repositories</Typography>
            </CardContent></Card>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Card><CardContent sx={{ textAlign: 'center' }}>
              <Insights color="info" sx={{ fontSize: 32, mb: 1 }} />
              <Typography variant="h4">{stats.avg_score?.toFixed(1) || '-'}</Typography>
              <Typography variant="body2" color="text.secondary">Avg Maturity Score</Typography>
            </CardContent></Card>
          </Grid>
        </Grid>
      )}

      {/* Tenant Lookup */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>Tenant Analytics</Typography>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <FormControl size="small" sx={{ flexGrow: 1 }}>
              <InputLabel>Select Tenant</InputLabel>
              <Select
                value={tenantId}
                label="Select Tenant"
                onChange={e => { setTenantId(e.target.value); }}
              >
                {tenants.map((t: any) => (
                  <MenuItem key={t.id} value={t.id}>{t.name} ({t.tier})</MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button variant="contained" onClick={fetchTenantAnalytics} disabled={!tenantId}>Load</Button>
          </Box>
        </CardContent>
      </Card>

      {/* RBAC-7 — Scanner-version distribution for the selected tenant.
          Surfaces "who's still on an old binary?" / "fleet drift across CI
          runners?" at a glance. Renders only when a tenant is loaded and
          has at least one analysis row. */}
      {scannerVersions.length > 0 && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
              <Typography variant="h6">Scanner versions in use</Typography>
              {scannerVersions.length === 1 ? (
                <Chip
                  label="single version (consistent fleet)"
                  size="small"
                  color="success"
                  variant="outlined"
                />
              ) : (
                <Chip
                  label={`${scannerVersions.length} versions in use`}
                  size="small"
                  color="warning"
                  variant="outlined"
                />
              )}
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
              Distinct scanner-client binary versions that have submitted scans
              for this tenant. NULL / "unknown" means scans from before the
              version column was added.
            </Typography>
            <Grid container spacing={1.5}>
              {scannerVersions.map((v) => (
                <Grid item xs={12} sm={6} md={4} key={v.scanner_version}>
                  <Tooltip
                    title={
                      v.scanner_version === 'unknown'
                        ? 'Scans from before scanner_version was persisted (migration 022, 2026-06-03).'
                        : `Used across ${v.distinct_repos} repo${v.distinct_repos === 1 ? '' : 's'}`
                    }
                  >
                    <Box
                      sx={{
                        p: 1.5,
                        border: '1px solid',
                        borderColor: v.scanner_version === 'unknown' ? 'grey.300' : 'primary.light',
                        borderRadius: 1,
                        bgcolor: v.scanner_version === 'unknown' ? 'grey.50' : 'transparent',
                      }}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                        <Typography
                          variant="subtitle2"
                          sx={{
                            fontFamily: 'monospace',
                            fontWeight: 600,
                            color: v.scanner_version === 'unknown' ? 'text.secondary' : 'text.primary',
                          }}
                        >
                          {v.scanner_version}
                        </Typography>
                        <Chip label={`${v.scan_count} scan${v.scan_count === 1 ? '' : 's'}`} size="small" />
                      </Box>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                        Repos: {v.distinct_repos}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        Last seen: {v.last_seen ? new Date(v.last_seen).toLocaleDateString() : '—'}
                      </Typography>
                    </Box>
                  </Tooltip>
                </Grid>
              ))}
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* INT-10 — Intent classification distribution.
          Renders only when the tenant has semantic intent detection
          enabled AND at least one agent has been classified.
          Empty state when semantic_intent_active=false explains why. */}
      {intentDist && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
              <Typography variant="h6">Agent fleet composition</Typography>
              {intentDist.semantic_intent_active ? (
                <Chip
                  label={`${intentDist.total_agents_classified} classified`}
                  size="small"
                  color="primary"
                  variant="outlined"
                />
              ) : (
                <Chip
                  label="semantic intent OFF"
                  size="small"
                  color="default"
                  variant="outlined"
                />
              )}
            </Box>

            {!intentDist.semantic_intent_active ? (
              <Typography variant="caption" color="text.secondary">
                This tenant has not enabled semantic intent detection.
                Classification of agents by job category requires the tenant
                to opt in (Settings → Intent Detection). Until then, agents
                appear in the registry but cannot be grouped by purpose.
              </Typography>
            ) : intentDist.total_agents_classified === 0 ? (
              <Typography variant="caption" color="text.secondary">
                Semantic intent is enabled but no agents have been scanned
                with full goal text yet. The next scan after enabling will
                start populating this view.
              </Typography>
            ) : (
              <>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                  Each agent classified into one of {intentDist.categories_available.length - 1} job categories
                  using the agent's declared role/goal text. "unknown" means the agent's purpose
                  didn't match any seeded category confidently enough.
                </Typography>
                <Grid container spacing={1.5}>
                  {intentDist.categories.map((cat) => {
                    const isUnknown = cat.category === 'unknown'
                    const pctOfFleet =
                      intentDist.total_agents_classified > 0
                        ? (cat.count / intentDist.total_agents_classified) * 100
                        : 0
                    return (
                      <Grid item xs={12} sm={6} md={4} key={cat.category}>
                        <Tooltip
                          title={
                            cat.agents.length > 0
                              ? `Examples: ${cat.agents
                                  .slice(0, 3)
                                  .map((a) => a.agent_name || a.agent_id.slice(0, 8))
                                  .join(', ')}`
                              : ''
                          }
                        >
                          <Box
                            sx={{
                              p: 1.5,
                              border: '1px solid',
                              borderColor: isUnknown ? 'grey.300' : 'primary.light',
                              borderRadius: 1,
                              bgcolor: isUnknown ? 'grey.50' : 'transparent',
                            }}
                          >
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                              <Typography
                                variant="subtitle2"
                                sx={{ fontWeight: 600, textTransform: 'capitalize' }}
                              >
                                {cat.category.replace(/-/g, ' ')}
                              </Typography>
                              <Chip
                                label={`${cat.count} agent${cat.count === 1 ? '' : 's'}`}
                                size="small"
                                color={isUnknown ? 'default' : 'primary'}
                              />
                            </Box>
                            <LinearProgress
                              variant="determinate"
                              value={pctOfFleet}
                              sx={{ mt: 1, height: 6, borderRadius: 3 }}
                            />
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ display: 'block', mt: 0.5 }}
                            >
                              {pctOfFleet.toFixed(1)}% of fleet
                            </Typography>
                          </Box>
                        </Tooltip>
                      </Grid>
                    )
                  })}
                </Grid>
              </>
            )}
          </CardContent>
        </Card>
      )}

      {/* §22 — tenant-wide cost rollup teaser. Renders nothing (not an
          empty state) when the tenant is below the gated tier — same
          "hide the section" principle as the rest of this feature (see
          docs/COST_ESTIMATION.md). Click-through passes tenant_id
          explicitly — this page is a cross-tenant admin lookup, and the
          Cost Analysis page otherwise defaults to the logged-in user's
          own tenant. */}
      {costRollup?.cost_estimation_visible && (
        <Card
          sx={{ mb: 3, cursor: 'pointer', '&:hover': { boxShadow: 4 } }}
          onClick={() => navigate(`/cost?tenant_id=${encodeURIComponent(tenantId)}`)}
        >
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Box sx={{
                width: 48, height: 48, borderRadius: '50%', display: 'flex',
                alignItems: 'center', justifyContent: 'center', mr: 2,
                bgcolor: 'success.light', color: 'success.dark',
              }}>
                <AttachMoney />
              </Box>
              <Box>
                <Typography variant="h4">
                  {costRollup.cost_per_request_usd
                    ? `$${fmtCost(costRollup.cost_per_request_usd.low)}–$${fmtCost(costRollup.cost_per_request_usd.high)}`
                    : '—'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Projected cost / request — {costRollup.repos_priced} of {costRollup.repos_total} repos
                </Typography>
                {costRollup.repos_priced > 0 && (
                  <Typography variant="caption" color="text.secondary">
                    {costRollup.repos_with_daily_estimate} of {costRollup.repos_priced} repos have a daily volume estimate
                    {costRollup.cost_per_day_usd && (
                      <> (${fmtCost(costRollup.cost_per_day_usd.low)}–${fmtCost(costRollup.cost_per_day_usd.high)}/day)</>
                    )}
                  </Typography>
                )}
              </Box>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Tenant-wide Trends — rich rendering of /trends/tenant/{id} payload */}
      {trends?.tenant_wide_trends && (() => {
        const tw = trends.tenant_wide_trends
        const overall = tw.overall_trend || {}
        const dims = tw.dimension_trends || {}
        const maturity = tw.maturity_progression || {}
        const fw = tw.framework_progression || {}
        const risk = tw.risk_progression || {}
        return (
          <>
            {/* Headline trend cards */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={6} md={3}>
                <Card><CardContent>
                  <Typography variant="caption" color="text.secondary">Overall Direction</Typography>
                  <Typography variant="h5" sx={{ color: directionColor(overall.direction || 'stable'), fontWeight: 600 }}>
                    {directionIcon(overall.direction || 'stable')} {overall.direction || 'stable'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Current: {(overall.current_score || 0).toFixed(1)}/10
                  </Typography>
                </CardContent></Card>
              </Grid>
              <Grid item xs={6} md={3}>
                <Card><CardContent>
                  <Typography variant="caption" color="text.secondary">Monthly Improvement</Typography>
                  <Typography variant="h5" sx={{ color: (overall.improvement_rate_monthly || 0) > 0 ? '#4caf50' : '#9e9e9e', fontWeight: 600 }}>
                    {(overall.improvement_rate_monthly || 0) >= 0 ? '+' : ''}{(overall.improvement_rate_monthly || 0).toFixed(2)}/mo
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Confidence: {((overall.confidence || 0) * 100).toFixed(0)}%
                  </Typography>
                </CardContent></Card>
              </Grid>
              <Grid item xs={6} md={3}>
                <Card><CardContent>
                  <Typography variant="caption" color="text.secondary">Maturity Level</Typography>
                  <Typography variant="h5" sx={{ fontWeight: 600, textTransform: 'capitalize' }}>
                    {maturity.current_level || 'unknown'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {(maturity.level_changes || []).length} level change{(maturity.level_changes || []).length !== 1 ? 's' : ''}
                  </Typography>
                </CardContent></Card>
              </Grid>
              <Grid item xs={6} md={3}>
                <Card><CardContent>
                  <Typography variant="caption" color="text.secondary">Risk Level</Typography>
                  <Typography variant="h5" sx={{ fontWeight: 600, textTransform: 'capitalize',
                    color: ['critical', 'high'].includes(risk.current_risk_level) ? '#f44336' : '#ff9800' }}>
                    {risk.current_risk_level || 'unknown'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Trend: {directionIcon((risk.risk_trend || {}).direction || 'stable')} {(risk.risk_trend || {}).direction || 'stable'}
                  </Typography>
                </CardContent></Card>
              </Grid>
            </Grid>

            {/* 6-Dimension trends */}
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>6-Dimension Trends</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                  Direction and slope per dimension over the last {trends.analysis_period_days || 90} days
                </Typography>
                <Grid container spacing={2}>
                  {Object.entries(DIMENSION_LABELS).map(([key, label]) => {
                    const t = (dims as any)[key] || { direction: 'stable', slope: 0, confidence: 0 }
                    return (
                      <Grid item xs={12} sm={6} md={4} key={key}>
                        <Box sx={{ p: 1.5, border: '1px solid', borderColor: 'grey.200', borderRadius: 1 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>{label}</Typography>
                            <Typography variant="body2" sx={{ color: directionColor(t.direction), fontWeight: 700 }}>
                              {directionIcon(t.direction)} {t.direction}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="caption" color="text.secondary">
                              Slope: {(t.slope || 0) >= 0 ? '+' : ''}{(t.slope || 0).toFixed(3)}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Confidence: {((t.confidence || 0) * 100).toFixed(0)}%
                            </Typography>
                          </Box>
                        </Box>
                      </Grid>
                    )
                  })}
                </Grid>
              </CardContent>
            </Card>

            {/* Framework progression */}
            {fw.current_framework_count !== undefined && (
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Framework Adoption</Typography>
                  <Box sx={{ display: 'flex', gap: 3, alignItems: 'center', mb: 2 }}>
                    <Box>
                      <Typography variant="h4" sx={{ fontWeight: 700 }}>{fw.current_framework_count}</Typography>
                      <Typography variant="caption" color="text.secondary">Current frameworks</Typography>
                    </Box>
                    <Box>
                      <Typography variant="h4" sx={{ fontWeight: 700 }}>{fw.diversity_score || 0}</Typography>
                      <Typography variant="caption" color="text.secondary">Total unique</Typography>
                    </Box>
                    <Box>
                      <Typography variant="body1" sx={{ color: directionColor((fw.adoption_trend || {}).direction || 'stable'), fontWeight: 600 }}>
                        {directionIcon((fw.adoption_trend || {}).direction || 'stable')} {(fw.adoption_trend || {}).direction || 'stable'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">Adoption trend</Typography>
                    </Box>
                  </Box>
                  {(fw.framework_changes || []).length > 0 && (
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>Recent changes:</Typography>
                      {(fw.framework_changes || []).slice(-5).map((c: any, i: number) => (
                        <Box key={i} sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 0.5 }}>
                          <Chip label={c.type} size="small" color={c.type === 'adoption' ? 'success' : 'default'} />
                          <Typography variant="body2">{(c.frameworks || []).join(', ')}</Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
                            {new Date(c.timestamp).toLocaleDateString()}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  )}
                </CardContent>
              </Card>
            )}
          </>
        )
      })()}

      {/* Trend insights — auto-generated from key_insights/recommendations */}
      {trends?.tenant_wide_trends && (
        ((trends.tenant_wide_trends.key_insights || []).length > 0 ||
         (trends.tenant_wide_trends.recommendations || []).length > 0) && (
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>Insights & Recommendations</Typography>
              {(trends.tenant_wide_trends.key_insights || []).map((ins: string, i: number) => (
                <Box key={`i-${i}`} sx={{ p: 1.5, mb: 1, bgcolor: 'success.50', borderRadius: 1, borderLeft: '3px solid', borderColor: 'success.main' }}>
                  <Typography variant="body2">{ins}</Typography>
                </Box>
              ))}
              {(trends.tenant_wide_trends.recommendations || []).map((rec: string, i: number) => (
                <Box key={`r-${i}`} sx={{ p: 1.5, mb: 1, bgcolor: 'warning.50', borderRadius: 1, borderLeft: '3px solid', borderColor: 'warning.main' }}>
                  <Typography variant="body2">{rec}</Typography>
                </Box>
              ))}
            </CardContent>
          </Card>
        )
      )}

      {/* Standalone insights from /insights/tenant/{id} (kept as-is — different concern from trend insights) */}
      {insights.length > 0 && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>Tenant Insights</Typography>
            {insights.map((ins: any, i: number) => (
              <Box key={i} sx={{ p: 1.5, mb: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Typography variant="subtitle2">{ins.title || ins.type}</Typography>
                <Typography variant="body2" color="text.secondary">{ins.message || ins.description}</Typography>
              </Box>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Milestones — prefer trends.recent_milestones (already loaded) but
          fall back to standalone /milestones endpoint if needed */}
      {(((trends?.recent_milestones || []).length > 0) || milestones.length > 0) && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>Milestones</Typography>
            {(trends?.recent_milestones?.length ? trends.recent_milestones : milestones).map((m: any, i: number) => (
              <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 1, borderBottom: '1px solid #eee' }}>
                <Star color="warning" />
                <Box sx={{ flexGrow: 1 }}>
                  <Typography variant="subtitle2">{m.title || m.name}</Typography>
                  {m.description && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      {m.description}
                    </Typography>
                  )}
                </Box>
                <Typography variant="caption" color="text.secondary">
                  {new Date(m.achieved_at || m.created_at).toLocaleDateString()}
                </Typography>
              </Box>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Empty state for tenants with no scans yet (need ≥2 for trends) */}
      {tenantId && trends && !trends.tenant_wide_trends && (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body2" color="text.secondary">
              No trend data yet — at least 2 scans within the last 90 days are needed to compute trends.
            </Typography>
          </CardContent>
        </Card>
      )}
    </Box>
  )
}

export default AnalyticsPage
